import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Store, Phone, ArrowRight, User, MapPin, Building2, Lock, PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import ShareAppButton from "@/components/ShareAppButton";
import PublicEventsSection from "@/components/events/PublicEventsSection";
import { AdBanner } from "@/components/AdBanner";
import { SiteFooter } from "@/components/SiteFooter";

export default function Login({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [authOpen, setAuthOpen] = useState(false);
  const openAuth = (m) => { setMode(m); setAuthOpen(true); };
  const [firmName, setFirmName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [needPassword, setNeedPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [adminPhone, setAdminPhone] = useState("");

  useEffect(() => {
    if (mode !== "login") return;
    const name = firmName.trim();
    if (name.length < 2) {
      setNeedPassword(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/auth/password-check", { params: { firm_name: name } });
        setNeedPassword(!!r.data.password_required);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
  }, [firmName, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload =
        mode === "signup"
          ? { firm_name: firmName, owner_name: ownerName, mobile, address, city }
          : needPassword
            ? { firm_name: firmName, password }
            : { firm_name: firmName, mobile };
      const r = await api.post(`/auth/${mode}`, payload);
      localStorage.setItem("ps_token", r.data.token);
      toast.success(`Welcome, ${r.data.shop.name}!`);
      onLogin(r.data.shop);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "PASSWORD_REQUIRED") {
        setNeedPassword(true);
        toast.info("Ye firm password se protected hai — apna password dalein");
      } else if (detail === "Incorrect password") {
        toast.error("Password galat hai — dobara try karein");
      } else {
        toast.error(typeof detail === "string" ? detail : "Request failed, please try again");
      }
    } finally {
      setBusy(false);
    }
  };

  const openForgot = async () => {
    setForgotOpen(true);
    try {
      const r = await api.get("/public/admin-contact");
      setAdminPhone(r.data.admin_phone || "");
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      <header className="bg-slate-900 text-white relative" data-testid="home-header">
        <div className="absolute top-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-cyan-500" />
          <div className="flex-1 bg-pink-600" />
          <div className="flex-1 bg-yellow-400" />
          <div className="flex-1 bg-slate-950" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.webp?v=2" alt="Sootrik" className="h-11 w-11 rounded-lg bg-white p-1 shrink-0" data-testid="login-brand-logo-mobile" />
            <div className="min-w-0">
              <p className="font-heading text-xl font-bold leading-tight" data-testid="login-brand-name-mobile">Sootrik <span className="text-cyan-400 font-semibold">– Digital Data Manager</span></p>
              <p className="text-xs text-slate-400 hidden sm:block">Event Digital Pass, ID/Certificate, Daily Order & More</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => openAuth("login")} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white" data-testid="home-login-btn">Login</Button>
            <Button onClick={() => openAuth("signup")} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="home-signup-btn">Sign Up</Button>
          </div>
        </div>
      </header>
      <div className="bg-slate-50 pt-6"><AdBanner /></div>
      <PublicEventsSection />
      <SiteFooter />
      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="max-w-md p-0 max-h-[92vh] overflow-y-auto" data-testid="auth-dialog">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <img src="/logo.webp?v=2" alt="Sootrik" className="h-10 w-10 rounded-lg" data-testid="login-card-brand-logo" />
              <span className="font-heading text-xl font-bold tracking-tight" data-testid="login-card-brand-name">Sootrik</span>
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight" data-testid="login-heading">
              {mode === "login" ? "Login" : "Sign Up"}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {mode === "login"
                ? "Enter User Name & Mobile Number"
                : "Fill User Name, Owner Name, Mobile Number,  Address & City"}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firm-name">User Name</Label>
                <div className="relative">
                  <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="firm-name"
                    data-testid="shop-login-name-input"
                    className="pl-9"
                    placeholder="User Name"
                    value={firmName}
                    onChange={(e) => setFirmName(e.target.value)}
                    required
                    minLength={2}
                  />
                </div>
              </div>
              {mode === "signup" && (
                <div className="space-y-2">
                  <Label htmlFor="owner-name">Owner Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="owner-name"
                      data-testid="signup-owner-name-input"
                      className="pl-9"
                      placeholder="Owner's name"
                      value={ownerName}
                      onChange={(e) => setOwnerName(e.target.value)}
                      required
                      minLength={2}
                    />
                  </div>
                </div>
              )}
              {(mode === "signup" || !needPassword) && (
              <div className="space-y-2">
                <Label htmlFor="shop-mobile">Mobile Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="shop-mobile"
                    data-testid="shop-login-mobile-input"
                    className="pl-9"
                    type="tel"
                    inputMode="numeric"
                    placeholder="10-digit mobile number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    required
                  />
                </div>
              </div>
              )}
              {mode === "login" && needPassword && (
                <div className="space-y-2">
                  <Label htmlFor="shop-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="shop-password"
                      data-testid="shop-login-password-input"
                      className="pl-9"
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={4}
                    />
                  </div>
                  <button
                    type="button"
                    data-testid="forgot-password-btn"
                    onClick={openForgot}
                    className="text-xs text-pink-700 hover:text-pink-800 font-semibold hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
              {mode === "signup" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="shop-address">Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="shop-address"
                        data-testid="signup-address-input"
                        className="pl-9"
                        placeholder="Address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shop-city">City</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="shop-city"
                        data-testid="signup-city-input"
                        className="pl-9"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </>
              )}
              <Button
                type="submit"
                data-testid="shop-login-submit-btn"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                disabled={busy}
              >
                {busy ? "Please wait..." : mode === "login" ? "Login" : "Sign Up"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogContent className="max-w-sm" data-testid="forgot-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading">Forgot Password</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-center">
                  <p className="text-sm text-muted-foreground">
                    Password reset karne ke liye admin ko call karein. Admin aapko ek link bhejega jisme aapki login details hongi.
                  </p>
                  {adminPhone ? (
                    <a href={`tel:${adminPhone}`} data-testid="forgot-call-btn" className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg px-5 py-2.5 font-semibold text-sm">
                      <PhoneCall className="h-4 w-4" /> Call Admin: {adminPhone}
                    </a>
                  ) : (
                    <p className="text-sm text-red-500" data-testid="forgot-no-phone">Admin contact abhi set nahi hai.</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <button
                type="button"
                data-testid="auth-mode-toggle"
                onClick={() => setMode(mode === "login" ? "signup" : "login")}
                className="text-pink-700 hover:text-pink-800 font-semibold hover:underline"
              >
                {mode === "login" ? "SignUP" : "Login"}
              </button>
              <ShareAppButton variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-400 hover:text-cyan-700 font-medium gap-1" testId="login-share-app-btn" />
              <button
                type="button"
                onClick={() => {
                  window.location.hash = "#admin";
                }}
                data-testid="login-admin-link"
                className="text-slate-400 hover:text-cyan-700 hover:underline inline-flex items-center gap-1 font-medium"
              >
                🛡️ Admin Panel
              </button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/download/windows-setup`}
                target="_blank"
                rel="noreferrer"
                className="text-cyan-700 hover:text-cyan-800 font-semibold inline-flex items-center gap-1 hover:underline"
              >
                📥 Download PC Setup (.bat)
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
